import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Loader2, Lock, Download, Zap, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

const STATUS_META = {
  open: { label: 'Open', cls: 'border-slate-300 text-slate-600' },
  'integriteits-issues': { label: 'Integriteits-issues', cls: 'border-amber-300 bg-amber-50 text-amber-700' },
  vergrendeld: { label: 'Vergrendeld', cls: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
  'geëxporteerd': { label: 'Aangeleverd', cls: 'border-sky-300 bg-sky-50 text-sky-700' },
};

export default function LoonrunCockpitSection({ loonYear, period, onLockedOrExported }) {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busySlug, setBusySlug] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/payroll/period/overview', { params: { loon_year: loonYear, period } });
      setItems(data.locations || []);
    } catch (e) {
      toast({ title: 'Overzicht laden mislukt', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [loonYear, period, toast]);

  useEffect(() => { load(); }, [load]);

  const lockOne = async (slug) => {
    setBusySlug(slug);
    try {
      await api.post('/payroll/period/lock', { location_id: slug, loon_year: loonYear, period });
      toast({ title: `${slug} vergrendeld` });
      await load();
      onLockedOrExported?.();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Vergrendelen mislukt', variant: 'destructive' });
    } finally {
      setBusySlug(null);
    }
  };

  const downloadOne = async (slug) => {
    setBusySlug(slug);
    try {
      const resp = await api.get('/payroll/period/export', {
        params: { location_id: slug, loon_year: loonYear, period }, responseType: 'blob',
      });
      const cd = resp.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `mutaties_TW_${slug}_${loonYear}_P${period}.xlsx`;
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      await load();
      onLockedOrExported?.();
    } catch (e) {
      toast({ title: 'Download mislukt', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBusySlug(null);
    }
  };

  const lockAllClean = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post('/payroll/period/lock-all', { loon_year: loonYear, period });
      const nLocked = data.locked?.length || 0;
      const nSkipped = data.skipped?.length || 0;
      toast({
        title: `${nLocked} locatie(s) vergrendeld`,
        description: nSkipped ? `${nSkipped} overgeslagen (integriteits-issues of al vergrendeld)` : undefined,
      });
      await load();
      onLockedOrExported?.();
    } catch (e) {
      toast({ title: 'Bulk-vergrendelen mislukt', variant: 'destructive' });
    } finally {
      setBulkBusy(false);
    }
  };

  const cleanCount = items.filter((i) => i.status === 'open').length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="loonrun-cockpit">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="loonrun-cockpit-toggle"
        className="flex w-full items-center justify-between px-4 py-3"
      >
        <span className="text-sm font-semibold text-slate-900">Loonrun-cockpit — alle locaties · periode {period} · {loonYear}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">{items.length} locatie(s) · {cleanCount} klaar om te vergrendelen</p>
            <Button
              type="button" size="sm" onClick={lockAllClean} disabled={bulkBusy || loading || cleanCount === 0}
              data-testid="loonrun-cockpit-lock-all"
              className="h-8 bg-brand-navy text-white hover:bg-brand-navy/90"
            >
              {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              <span className="ml-1.5">Vergrendel alles wat schoon is</span>
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="loonrun-cockpit-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-1.5 pr-3">Locatie</th>
                    <th className="py-1.5 pr-3">Status</th>
                    <th className="py-1.5 pr-3">Sync</th>
                    <th className="py-1.5 pr-3">Aangeleverd</th>
                    <th className="py-1.5 pr-3 text-right">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const meta = STATUS_META[it.status] || STATUS_META.open;
                    const canLock = it.status === 'open';
                    const canDownload = it.status === 'vergrendeld' || it.status === 'geëxporteerd';
                    return (
                      <tr key={it.location_slug} className="border-b border-slate-100" data-testid={`loonrun-cockpit-row-${it.location_slug}`}>
                        <td className="py-1.5 pr-3 font-medium text-slate-800">{it.location_name}</td>
                        <td className="py-1.5 pr-3">
                          <Badge variant="outline" className={`gap-1 ${meta.cls}`} data-testid={`loonrun-cockpit-status-${it.location_slug}`}>
                            {it.status === 'integriteits-issues' && <AlertTriangle size={11} />}
                            {meta.label}{it.status === 'integriteits-issues' && it.issue_count ? ` (${it.issue_count})` : ''}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-slate-500">
                          {it.in_sync === null ? '—' : it.in_sync ? 'OK' : `drift (${it.drift_count})`}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-slate-500">
                          {it.exported_at ? new Date(it.exported_at).toLocaleDateString('nl-NL') : '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              type="button" size="sm" variant="outline" disabled={!canLock || busySlug === it.location_slug}
                              onClick={() => lockOne(it.location_slug)} data-testid={`loonrun-cockpit-lock-${it.location_slug}`}
                              className="h-7 text-xs"
                            >
                              {busySlug === it.location_slug ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                              <span className="ml-1">Vergrendelen</span>
                            </Button>
                            <Button
                              type="button" size="sm" variant="outline" disabled={!canDownload || busySlug === it.location_slug}
                              onClick={() => downloadOne(it.location_slug)} data-testid={`loonrun-cockpit-download-${it.location_slug}`}
                              className="h-7 text-xs"
                            >
                              {busySlug === it.location_slug ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                              <span className="ml-1">Downloaden</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
